import { Injectable } from '@angular/core';

interface FlagPayload {
  ts: number;
}

/**
 * Detects an upload interrupted by a full page reload/close and lets the app show a
 * one-time warning toast on the next load. Backed by sessionStorage (tab-scoped, survives
 * a reload of the same tab, discarded on tab close) since the beforeunload write must be
 * synchronous.
 */
@Injectable({
  providedIn: 'root'
})
export class UploadInterruptionService {
  private readonly IN_PROGRESS_KEY = 'uploadInProgress';
  private readonly INTERRUPTED_KEY = 'uploadInterrupted';
  private readonly STALE_MS = 5 * 60 * 1000;
  private listenersRegistered = false;

  private readonly onBeforeUnload = (): void => {
    if (this.isUploadActive()) {
      this.writeFlag(this.INTERRUPTED_KEY);
    }
  };

  /** Idempotent; call once from the app root so listeners aren't re-added per component instance. */
  registerBeforeUnloadListener(): void {
    if (this.listenersRegistered) {
      return;
    }
    this.listenersRegistered = true;
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('pagehide', this.onBeforeUnload);
  }

  markUploadActive(): void {
    if (!this.isUploadActive()) {
      this.writeFlag(this.IN_PROGRESS_KEY);
    }
  }

  /** Call once the in-progress map is known to be empty/non-empty after a terminal state change. */
  clearUploadActiveIfIdle(hasActiveUploads: boolean): void {
    if (!hasActiveUploads) {
      this.removeFlag(this.IN_PROGRESS_KEY);
    }
  }

  /** Reads and clears both flags atomically; returns true only for a genuine, fresh interruption. */
  consumeInterruptionIfAny(): boolean {
    const flag = this.readFlag(this.INTERRUPTED_KEY);
    this.removeFlag(this.INTERRUPTED_KEY);
    this.removeFlag(this.IN_PROGRESS_KEY);
    return !!flag && Date.now() - flag.ts <= this.STALE_MS;
  }

  private isUploadActive(): boolean {
    return this.readFlag(this.IN_PROGRESS_KEY) !== null;
  }

  private writeFlag(key: string): void {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now() } as FlagPayload));
    } catch {
      /* storage unavailable (e.g. strict privacy mode); degrade silently */
    }
  }

  private removeFlag(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* no-op */
    }
  }

  private readFlag(key: string): FlagPayload | null {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
